/**
 * Versioned CTA/disclosure text. Every consent record stores the disclosureVersion
 * it was collected under, so we can later prove which language and which
 * exact wording the contact agreed to.
 */

export type CampaignType = "political" | "nonprofit" | "employer" | "commercial";
export type Channel = "sms" | "whatsapp";
export type Language = "en" | "es";

export interface Disclosure {
  version: string;
  campaignType: CampaignType;
  channel: Channel;
  language: Language;
  cta: string;            // shown at point of opt-in
  helpReply: string;      // returned on HELP
  stopReply: string;      // returned on STOP
  optInConfirmation: string;
}

const v = "2026-01-15.v1";

export const DISCLOSURES: Disclosure[] = [
  {
    version: v, campaignType: "political", channel: "sms", language: "en",
    cta: "By providing your number, you agree to receive recurring survey or campaign texts from {clientName}. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.",
    helpReply: "{clientName}: Reply STOP to stop. Msg&data rates may apply. Support: {supportEmail}",
    stopReply: "You have been unsubscribed from {clientName} and will receive no more messages.",
    optInConfirmation: "{clientName}: You're subscribed to campaign updates. Msg freq varies. Reply HELP for help, STOP to cancel.",
  },
  {
    version: v, campaignType: "political", channel: "sms", language: "es",
    cta: "Al proporcionar su número, acepta recibir mensajes de texto recurrentes de encuestas o campañas de {clientName}. La frecuencia varía. Pueden aplicarse tarifas por mensajes y datos. Responda STOP para cancelar o HELP para ayuda.",
    helpReply: "{clientName}: Responda STOP para cancelar. Pueden aplicar tarifas. Soporte: {supportEmail}",
    stopReply: "Ha cancelado la suscripción de {clientName} y no recibirá más mensajes.",
    optInConfirmation: "{clientName}: Está suscrito a actualizaciones de campaña. Frecuencia variable. Responda HELP para ayuda, STOP para cancelar.",
  },
  {
    version: v, campaignType: "nonprofit", channel: "sms", language: "en",
    cta: "Sign up to receive updates from {clientName}. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.",
    helpReply: "{clientName}: Reply STOP to stop. Support: {supportEmail}",
    stopReply: "You have been unsubscribed from {clientName}.",
    optInConfirmation: "{clientName}: You're subscribed. Reply HELP for help, STOP to cancel.",
  },
  {
    version: v, campaignType: "nonprofit", channel: "sms", language: "es",
    cta: "Suscríbase para recibir actualizaciones de {clientName}. La frecuencia varía. Pueden aplicarse tarifas. Responda STOP para cancelar o HELP para ayuda.",
    helpReply: "{clientName}: Responda STOP para cancelar. Soporte: {supportEmail}",
    stopReply: "Ha cancelado la suscripción de {clientName}.",
    optInConfirmation: "{clientName}: Está suscrito. Responda HELP para ayuda, STOP para cancelar.",
  },
  {
    version: v, campaignType: "employer", channel: "sms", language: "en",
    cta: "By providing your number, you agree to receive workplace survey and notification texts from {clientName}. Msg freq varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.",
    helpReply: "{clientName} HR: Reply STOP to stop. Support: {supportEmail}",
    stopReply: "You've opted out of {clientName} workplace messages.",
    optInConfirmation: "{clientName}: You're subscribed to workplace notifications. Reply HELP for help, STOP to cancel.",
  },
  {
    version: v, campaignType: "employer", channel: "sms", language: "es",
    cta: "Al proporcionar su número, acepta recibir mensajes de encuestas y notificaciones laborales de {clientName}. Frecuencia variable. Pueden aplicar tarifas. STOP para cancelar, HELP para ayuda.",
    helpReply: "{clientName} RH: Responda STOP para cancelar. Soporte: {supportEmail}",
    stopReply: "Ha cancelado los mensajes laborales de {clientName}.",
    optInConfirmation: "{clientName}: Suscrito a notificaciones laborales. Responda HELP, STOP para cancelar.",
  },
  {
    version: v, campaignType: "commercial", channel: "sms", language: "en",
    cta: "By providing your number, you agree to receive recurring marketing texts from {clientName}. Consent is not a condition of purchase. Msg freq varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.",
    helpReply: "{clientName}: Reply STOP to stop. Support: {supportEmail}",
    stopReply: "You've been unsubscribed from {clientName} marketing messages.",
    optInConfirmation: "{clientName}: You're subscribed. Reply HELP for help, STOP to cancel.",
  },
  {
    version: v, campaignType: "commercial", channel: "sms", language: "es",
    cta: "Al proporcionar su número, acepta recibir mensajes de marketing recurrentes de {clientName}. El consentimiento no es condición de compra. Frecuencia variable. Pueden aplicar tarifas. STOP para cancelar, HELP para ayuda.",
    helpReply: "{clientName}: Responda STOP para cancelar. Soporte: {supportEmail}",
    stopReply: "Ha cancelado los mensajes de marketing de {clientName}.",
    optInConfirmation: "{clientName}: Suscrito. Responda HELP, STOP para cancelar.",
  },
  // WhatsApp variants (opt-in is separate per Meta policy)
  {
    version: v, campaignType: "political", channel: "whatsapp", language: "en",
    cta: "Opt in to receive campaign updates from {clientName} on WhatsApp. You can stop messages anytime by replying STOP or using WhatsApp's block/report controls.",
    helpReply: "{clientName}: Reply STOP to stop. Support: {supportEmail}",
    stopReply: "You have been unsubscribed from {clientName} on WhatsApp.",
    optInConfirmation: "{clientName}: You're opted in for WhatsApp updates. Reply STOP to cancel.",
  },
  {
    version: v, campaignType: "political", channel: "whatsapp", language: "es",
    cta: "Acepte recibir actualizaciones de campaña de {clientName} por WhatsApp. Puede cancelar en cualquier momento respondiendo STOP o usando los controles de bloqueo de WhatsApp.",
    helpReply: "{clientName}: Responda STOP para cancelar. Soporte: {supportEmail}",
    stopReply: "Ha cancelado la suscripción de {clientName} en WhatsApp.",
    optInConfirmation: "{clientName}: Suscrito a actualizaciones por WhatsApp. Responda STOP para cancelar.",
  },
];

export function getDisclosure(
  campaignType: CampaignType,
  channel: Channel,
  language: Language,
): Disclosure | undefined {
  return DISCLOSURES.find(
    (d) => d.campaignType === campaignType && d.channel === channel && d.language === language,
  );
}

export function renderDisclosure(
  d: Disclosure,
  vars: { clientName: string; supportEmail: string },
): Disclosure {
  const fill = (s: string) =>
    s.replaceAll("{clientName}", vars.clientName).replaceAll("{supportEmail}", vars.supportEmail);
  return {
    ...d,
    cta: fill(d.cta),
    helpReply: fill(d.helpReply),
    stopReply: fill(d.stopReply),
    optInConfirmation: fill(d.optInConfirmation),
  };
}
